
import React, {Component} from 'react';
import {View, Text, Image, TouchableOpacity} from 'react-native';
import {fontFamily} from '../../helpers/fontFamily';
import {itemKey} from './boxChampProjection';

const colors = {
  zoneHeader: '#aab7c4',
  completedBg: 'rgba(0,0,0,0.35)',
  completedText: '#9c9c9c',
  completedCheck: '#7ed321',
  activeBorder: '#5f80ff',
  activeBg: 'rgba(95,128,255,0.12)',
  text: '#ffffff',
  mutedText: '#c7c7c7',
};

const zoneHeaderStyle = {
  color: colors.zoneHeader,
  fontSize: 13,
  fontFamily: fontFamily.font600,
  textTransform: 'uppercase',
  letterSpacing: 1,
  marginHorizontal: 8,
  marginTop: 8,
  marginBottom: 2,
};

export default class BoxChampZonedList extends Component {
  constructor(props) {
    super(props);
    this.state = {expandedCompleted: {}, pages: {completed: 1, upcoming: 1}};
  }

  labelFor = row => {
    const {labels, labelsByProduct} = this.props.view;
    const entry =
      labels[itemKey(row.order_item_id, row.kit_id)] ||
      (labelsByProduct || {})[row.product_id];
    return (entry && (entry.name || entry.sku)) || 'Item';
  };

  static PAGE_SIZE = 8;

  pageOf = (zone, groups) => {
    const limit = (this.state.pages[zone] || 1) * BoxChampZonedList.PAGE_SIZE;
    return {shown: groups.slice(0, limit), hidden: Math.max(groups.length - limit, 0)};
  };

  showMore = zone =>
    this.setState(prev => ({
      pages: {...prev.pages, [zone]: (prev.pages[zone] || 1) + 1},
    }));

  renderShowMore(zone, hidden) {
    if (hidden <= 0) return null;
    return (
      <TouchableOpacity
        testID={`boxchamp-${zone}-show-more`}
        onPress={() => this.showMore(zone)}
        style={{marginHorizontal: 6, marginBottom: 6, paddingVertical: 8, alignItems: 'center'}}>
        <Text style={{color: colors.zoneHeader, fontSize: 13, fontFamily: fontFamily.font600}}>
          Show {hidden} more
        </Text>
      </TouchableOpacity>
    );
  }

  toggleCompleted = key => {
    this.setState(prev => ({
      expandedCompleted: {...prev.expandedCompleted, [key]: !prev.expandedCompleted[key]},
    }));
  };

  renderCompleted(allGroups) {
    if (!allGroups.length) return null;
    const {shown: groups, hidden} = this.pageOf('completed', allGroups);
    return (
      <View testID="boxchamp-zone-completed">
        <Text style={zoneHeaderStyle}>Packed</Text>
        {groups.map(group => {
          const expanded = !!this.state.expandedCompleted[group.key];
          return (
            <View key={`completed-${group.key}`} style={{marginHorizontal: 6, marginBottom: 4}}>
              <TouchableOpacity
                testID={`boxchamp-completed-group-${group.key}`}
                onPress={() => this.toggleCompleted(group.key)}
                style={{
                  backgroundColor: colors.completedBg,
                  borderRadius: 8,
                  paddingHorizontal: 10,
                  paddingVertical: 8,
                  flexDirection: 'row',
                  alignItems: 'center',
                }}>
                <Text style={{color: colors.completedCheck, fontSize: 15, marginRight: 8}}>✓</Text>
                <Text style={{color: colors.completedText, fontSize: 14, fontFamily: fontFamily.font600, flex: 1}}>
                  {group.count > 1 ? `${group.count} × ` : ''}{group.name} — {
                    group.boxes.every(e => e.sealed) ? 'Packed' : 'Ready to seal'
                  }
                </Text>
                <Text style={{color: colors.completedText, fontSize: 12}}>
                  {expanded ? '▲' : '▼'}
                </Text>
              </TouchableOpacity>
              {expanded &&
                group.boxes.map(entry => (
                  <View
                    key={`completed-box-${entry.box.id}`}
                    testID={`boxchamp-completed-box-${entry.box.id}`}
                    style={{paddingLeft: 26, paddingVertical: 4}}>
                    {group.count > 1 && (
                      <Text style={{color: colors.completedText, fontSize: 12, fontFamily: fontFamily.font600}}>
                        {entry.box.name}
                      </Text>
                    )}
                    {entry.rows.map(r => (
                      <Text
                        key={`completed-row-${r.row.id}`}
                        style={{color: colors.completedText, fontSize: 13}}>
                        {this.labelFor(r.row)}  {r.filled}/{r.target}
                      </Text>
                    ))}
                  </View>
                ))}
            </View>
          );
        })}
        {this.renderShowMore('completed', hidden)}
      </View>
    );
  }

  renderActive(active) {
    if (!active) return null;
    const {renderItem, view} = this.props;
    const boxImage = active.box && active.box.image;
    return (
      <View
        testID="boxchamp-zone-active"
        style={{
          borderWidth: 2,
          borderColor: colors.activeBorder,
          backgroundColor: colors.activeBg,
          borderRadius: 10,
          margin: 6,
          paddingBottom: 4,
        }}>
        <View style={{flexDirection: 'row', alignItems: 'center', padding: 8}}>
          {!!boxImage && (
            <Image
              testID="boxchamp-active-box-image"
              source={{uri: boxImage}}
              style={{width: 42, height: 42, borderRadius: 6, marginRight: 10, backgroundColor: '#fff'}}
              resizeMode="contain"
            />
          )}
          <View style={{flex: 1}}>
            <Text style={{color: colors.text, fontSize: 16, fontFamily: fontFamily.font600}}>
              {active.box && active.box.name}
            </Text>
            <Text
              testID="boxchamp-active-position"
              style={{
                color: colors.activeBorder,
                fontSize: 13,
                fontFamily: fontFamily.font600,
                marginTop: 2,
              }}>
              {active.positionLabel}
            </Text>
          </View>
          <Text
            testID="boxchamp-active-progress"
            style={{color: colors.text, fontSize: 15, fontFamily: fontFamily.font600}}>
            {active.filled}/{active.planned}
          </Text>
          {/* A quiet "more actions" affordance: a bordered pill here competed
              with the position label and cluttered an information header. */}
          {!!this.props.onOpenOptions && (
            <TouchableOpacity
              testID="boxchamp-open-options"
              onPress={this.props.onOpenOptions}
              accessibilityLabel="Box options"
              style={{
                marginLeft: 10,
                paddingHorizontal: 6,
                paddingVertical: 2,
                borderRadius: 4,
              }}>
              <Text
                style={{
                  color: colors.mutedText,
                  fontSize: 20,
                  lineHeight: 20,
                  fontFamily: fontFamily.font600,
                }}>
                ⋯
              </Text>
            </TouchableOpacity>
          )}
        </View>
        <View style={{paddingHorizontal: 10, paddingBottom: 4}}>
          {active.rows.map(r => (
            <Text
              key={`active-row-${r.row.id}`}
              testID={`boxchamp-active-row-${r.row.id}`}
              style={{
                color: r.filled >= r.target ? colors.completedCheck : colors.mutedText,
                fontSize: 13,
              }}>
              {this.labelFor(r.row)}  {r.filled}/{r.target}
            </Text>
          ))}
        </View>
        {view.activeItems.map((item, index) => (
          <View key={`active-card-${item.order_item_id}-${index}`}>{renderItem(item, index)}</View>
        ))}
      </View>
    );
  }

  renderUpcoming(allGroups) {
    if (!allGroups.length) return null;
    const {shown: groups, hidden} = this.pageOf('upcoming', allGroups);
    return (
      <View testID="boxchamp-zone-upcoming">
        <Text style={zoneHeaderStyle}>Up Next</Text>
        {groups.map(group => (
          <View
            key={`upcoming-${group.key}`}
            testID={`boxchamp-upcoming-group-${group.key}`}
            style={{
              backgroundColor: 'rgba(255,255,255,0.06)',
              borderRadius: 8,
              marginHorizontal: 6,
              marginBottom: 4,
              paddingHorizontal: 10,
              paddingVertical: 8,
            }}>
            <Text style={{color: colors.text, fontSize: 14, fontFamily: fontFamily.font600}}>
              {group.count > 1 ? `${group.count} × ` : ''}{group.name}
            </Text>
            {group.boxes[0].rows.map(r => (
              <Text
                key={`upcoming-row-${r.row.id}`}
                style={{color: colors.mutedText, fontSize: 13}}>
                {this.labelFor(r.row)} × {Number(r.row.item_qty) || 0}
              </Text>
            ))}
          </View>
        ))}
        {this.renderShowMore('upcoming', hidden)}
      </View>
    );
  }

  renderUnboxed(items) {
    if (!items.length) return null;
    const {renderItem, view} = this.props;
    const offset = view.activeItems.length;
    return (
      <View testID="boxchamp-zone-unboxed">
        <Text style={zoneHeaderStyle}>Unboxed Items</Text>
        {items.map((item, index) => (
          <View key={`unboxed-card-${item.order_item_id}-${index}`}>{renderItem(item, offset + index)}</View>
        ))}
      </View>
    );
  }

  render() {
    const {view} = this.props;
    if (!view) return null;
    const {zones, unboxedItems} = view;
    return (
      <View testID="boxchamp-zones">
        {this.renderCompleted(zones.completed)}
        {this.renderActive(zones.active)}
        {this.renderUpcoming(zones.upcoming)}
        {this.renderUnboxed(unboxedItems)}
      </View>
    );
  }
}
